// React
import { useEffect, useState } from "react";

// Utils
import { sanitizeItemsData } from "@Settings/helpers/AdministrationTools/DealerAdministrationTools/publishInventory.ts";

// Component
import SmartSelector from "@Layouts/components/InputComponents/SmartSelector";

// Types
import { IDMSMappingOption, IPublishInventoryRow } from "@Settings/types/administrationTools/dealerManagementSystem.ts";
import { GridRowModesModel } from "@mui/x-data-grid/models/api/gridEditingApi";

type ReportGroupSelectorProps = {
	id: string;
	title: string;
	selectLabel: string;
	currentData: IPublishInventoryRow[];
	dmsMappingOptions: IDMSMappingOption[];
	row: IPublishInventoryRow;
	rowModesModel: GridRowModesModel;
	// Callbacks
	onDataUpdate: Function;
	onRenderUpdate: Function;
};

type ReportGroupOption = {
	status: string;
	label: string;
};

function getDMSPublisherSelectorOptions(mappingOptions: IDMSMappingOption[], sectionTitle: string) {
	return mappingOptions?.find((dmsOption) => dmsOption.vehicleType === sectionTitle)?.options ?? [];
}

function convertString(stringVal: string) {
	if (stringVal) {
		const words = stringVal.split("-");
		return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join("-");
	}
	return stringVal;
}

export default function ReportGroupSelector(props: ReportGroupSelectorProps) {
	const {
		id,
		currentData,
		dmsMappingOptions,
		row,
		rowModesModel,
		title,
		selectLabel,
		// Callback
		onDataUpdate,
		onRenderUpdate,
	} = props;
	const [selectedOption, setSelectedOption] = useState<ReportGroupOption | null>(null);

	/********************************************* HANDLERS ************************************************/
	const handleChange = async (dataObject: ReportGroupOption) => {
		const newValue = dataObject.status;
		const updatedItems = currentData.map((item) => (item.id === id ? { ...item, mappedName: newValue } : item));

		if (rowModesModel && rowModesModel[id]?.mode !== "edit") {
			// is view mode
			const sanitizedItems = sanitizeItemsData(updatedItems);
			const dataToSave = {
				[title]: sanitizedItems,
			};

			await onDataUpdate(id, dataToSave);
		}

		onRenderUpdate(updatedItems);
	};

	useEffect(() => {
		const getObjectVal = (stringVal = "") => {
			if (!stringVal) {
				return {
					status: "",
					label: selectLabel || "--",
				};
			}
			return {
				status: stringVal,
				label: convertString(stringVal),
			};
		};

		setSelectedOption(getObjectVal(row?.mappedName));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [row?.mappedName]);

	return (
		<SmartSelector
			selectBy={"status"}
			options={getDMSPublisherSelectorOptions(dmsMappingOptions, title) ?? []}
			selected={selectedOption}
			inputProps={{
				placeholder: "",
				classes:
					"!bg-transparent [&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] [&_.MuiInputBase-root_.MuiInputBase-input]:!bg-transparent [&_.MuiInputBase-root_.MuiInputBase-input]:!rounded-none [&_.MuiInputBase-root>fieldset]:!border-none",
			}}
			listBoxProps={{
				sx: { maxHeight: "220px" },
			}}
			clearable
			disabled={!row.isEditable}
			onChangeAction={(value) => handleChange(value as unknown as ReportGroupOption)}
		/>
	);
}
